// Undo and rewind (issue 1169; Tom's B): the DM steps a scene back to an earlier moment. A rewind is a NEW event on
// the scene's log, never a deletion: the server rebuilds the scene as it stood at the moment (the nearest snapshot
// before it, then the log replayed) and STAMPS on the event what changed from now to then — the board's parts, each
// whole, and the objects by id — so a replay applies the stamp and never works the past out again. What a rewind
// restores is the board (tokens and everything on them, the grid, fog and explored squares, walls, doors, lights, the
// weather, zones, drawings, focuses and areas, placement) and the encounter (the turn order and its effects). What it
// never touches is the table's own log — rolls, asks, rulings, handouts and their pins, the sound, the cues, the rules:
// a roll made in the rewound minutes stays in the log, marked as made before the rewind (`server/src/dice.ts`).
import { withDefaults } from './objects.js';
/** The board's parts a rewind restores, each replaced whole when it changed (`objects` is by id, below). */
export const REWOUND_PARTS = ['grid', 'scene', 'encounter', 'focuses', 'areas', 'drawings', 'placing', 'fog', 'walls', 'vision', 'lightLevel', 'lights', 'weather', 'zones'];
/**
 * The kinds a rewind leaves standing: the table's log (whichever scene's log they are on) and the pins. Undo looks past
 * them for the DM's newest act, and the moments a DM may rewind to are the changes that are not these.
 */
export const KEPT_KINDS = new Set([
    'roll', 'roll.amend', 'log.clear', 'log.note', 'roll.ask', 'roll.ask.close', 'attack.ac', 'attack.answer', 'attack.apply', 'save.bonus', 'save.apply', 'test.karma', 'roll.apply', 'roll.settle',
    'table.rules', 'ruling.add', 'ruling.keep', 'ruling.remove', 'session.end', 'handout.set', 'handout.send', 'handout.take', 'handout.delete',
    // The cards (issue 1177) are the table's: a rewind never deals them back.
    'deck.add', 'deck.shuffle', 'deck.remove', 'deck.draw', 'card.discard', 'card.play', 'card.pass', 'card.reveal',
    'pin.set', 'pin.remove', 'pin.audience', 'link.set', 'link.remove', 'audio.play', 'audio.next', 'audio.stop', 'cue.set', 'cue.remove',
]);
/**
 * The most a stamp may weigh, in characters of its STORED form (`storedLength`, jsonb's text, ~15% longer than
 * `JSON.stringify`): a stored event is at most 65,536 (vtt/002-events.xml), and the rest of the event (its kind, the
 * moment) needs room. A rewind whose changes are heavier is refused; one that a sight stamp then takes past the limit
 * is refused by the commit's own size check, with the same words.
 */
export const REWIND_STAMP_MAX = 65_000;
export const REWIND_TOO_BIG = 'Too much has changed on this scene since then to restore it in one step. Choose a later moment, then rewind again.';
export const NOTHING_TO_REWIND = 'Nothing on this scene has changed since then.';
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/**
 * What to stamp on a rewind from `now` (the state it meets) to `then` (the moment's). The fog's version moves on past
 * now's when the fog changes, never back: a masked map is cached by it (`?v=`), and an old version names an old mask.
 */
export function rewindStamp(now, then) {
    const out = {};
    for (const k of REWOUND_PARTS) {
        if (k === 'fog')
            continue;
        const was = then[k] ?? null;
        const is = now[k] ?? null;
        if (!same(was, is))
            out[k] = then[k] ?? null;
    }
    const { rev: _r1, ...fogThen } = then.fog;
    const { rev: _r2, ...fogNow } = now.fog;
    if (!same(fogThen, fogNow))
        out.fog = { ...then.fog, rev: now.fog.rev + 1 };
    const set = [];
    const gone = [];
    for (const [id, o] of Object.entries(then.objects)) {
        if (same(o, now.objects[id]))
            continue;
        // A sheet-linked token's hit points are on its character's sheet too, which no rewind turns back: restored to other
        // numbers than it holds now, they are not on the sheet, and its player is asked as after any change at the table.
        const was = now.objects[id];
        // The nonlethal count (issue 1502) is restored as the hit points are: other than now, not on the sheet.
        const hpBack = o.character && was?.character === o.character && (o.values?.hp?.current !== was.values?.hp?.current || (o.values?.hp?.nonlethal ?? 0) !== (was.values?.hp?.nonlethal ?? 0));
        set.push(hpBack && !o.hpOffSheet ? { ...o, hpOffSheet: true } : o);
    }
    for (const id of Object.keys(now.objects))
        if (!then.objects[id])
            gone.push(id);
    if (set.length || gone.length)
        out.objects = { set, gone };
    return out;
}
/** Whether a stamp changes nothing. */
export const rewindsNothing = (s) => Object.keys(s).length === 0;
/** A state with a rewind's stamp applied: the parts replaced, the objects set and gone, the DM's undelete dropped. */
export function applyRewind(state, stamp) {
    if (!stamp)
        return state;
    const next = { ...state, undelete: null };
    for (const k of REWOUND_PARTS)
        if (k in stamp)
            next[k] = stamp[k];
    if (stamp.objects) {
        const objects = { ...state.objects };
        for (const id of stamp.objects.gone)
            delete objects[id];
        for (const o of stamp.objects.set)
            objects[o.id] = o;
        next.objects = objects;
    }
    return next;
}
/** A stored stamp read back, each part through the snapshot's own reader (`withDefaults`); null for a shape that is not one. */
export function parseRewindStamp(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const raw = v;
    const objs = raw.objects;
    if (objs !== undefined && (typeof objs !== 'object' || objs === null || !Array.isArray(objs.set) || !Array.isArray(objs.gone) || !objs.gone.every((x) => typeof x === 'string')))
        return null;
    const setIn = (objs?.set ?? []);
    if (!setIn.every((o) => typeof o === 'object' && o !== null && typeof o.id === 'string'))
        return null;
    const read = withDefaults({ ...raw, objects: Object.fromEntries(setIn.map((o) => [o.id, o])) });
    const out = {};
    for (const k of REWOUND_PARTS)
        if (k in raw)
            out[k] = k === 'weather' && raw.weather === null ? null : read[k];
    if (objs)
        out.objects = { set: setIn.map((o) => read.objects[o.id]), gone: [...objs.gone] };
    return out;
}
/** A moment from the wire or the log: a timestamp, to the microsecond, that `Date` reads. */
export const isMoment = (v) => typeof v === 'string' && v.length <= 40 && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d{1,6})?(Z|[+-]\d\d:?\d\d)$/.test(v) && !Number.isNaN(Date.parse(v));
//# sourceMappingURL=rewind.js.map